import { Injectable } from '@angular/core';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { GetTokenSilentlyOptions } from '@auth0/auth0-spa-js';
import { Observable, of, from } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { FrontendLoggerService } from './frontend-logger.service';

@Injectable({ providedIn: 'root' })
export class TokenService {
  private cachedAccessToken: string | null = null;
  private cachedAccessTokenExpMs: number | null = null;
  private tokenRequest$: Observable<string> | null = null;

  constructor(
    private readonly auth0: Auth0Service,
    private readonly frontendLogger: FrontendLoggerService
  ) {}

  getToken(options?: GetTokenSilentlyOptions): Observable<string> {
    const cachedToken = this.getCachedValidToken();
    if (cachedToken) {
      this.frontendLogger.debug('[TokenService] getToken(): returning cached token');
      return of(cachedToken);
    }

    if (this.tokenRequest$) {
      this.frontendLogger.debug('[TokenService] getToken(): reusing in-flight token request');
      return this.tokenRequest$;
    }

    const params: GetTokenSilentlyOptions = {
      ...options,
      authorizationParams: {
        ...(environment.auth0.authorizationParams?.audience ? { audience: environment.auth0.authorizationParams.audience } : {}),
        ...(environment.auth0.authorizationParams?.scope ? { scope: environment.auth0.authorizationParams.scope } : {}),
        ...((options?.authorizationParams as Record<string, unknown> | undefined) ?? {}),
      },
    };
    this.frontendLogger.debug(`[TokenService] getToken(): requesting token with params: ${JSON.stringify(params.authorizationParams)}`);
    this.tokenRequest$ = from(this.auth0.getAccessTokenSilently(params)).pipe(
      tap((t) => this.frontendLogger.debug(`[TokenService] getToken(): token obtained len=${(typeof t === 'string' ? t : (t as any)?.access_token || '').length}`)),
      map((t) => this.extractToken(t)),
      map((token) => this.storeOrFallbackToCachedToken(token, 'empty response')),
      catchError((err) => {
        const reason = JSON.stringify(err?.message || err);
        this.frontendLogger.warn(`[TokenService] getToken(): failed to obtain token: ${reason}`);
        const cachedFallback = this.getCachedValidToken();
        if (cachedFallback) {
          this.frontendLogger.warn('[TokenService] getToken(): using cached token after silent token failure.');
          return of(cachedFallback);
        }
        return of('');
      }),
      finalize(() => {
        this.tokenRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.tokenRequest$;
  }

  private extractToken(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (value && typeof value === 'object' && 'access_token' in value) {
      const accessToken = (value as { access_token?: unknown }).access_token;
      return typeof accessToken === 'string' ? accessToken : '';
    }

    return '';
  }

  private storeOrFallbackToCachedToken(token: string, reason: string): string {
    const normalizedToken = token.trim();
    if (normalizedToken) {
      this.cacheToken(normalizedToken);
      return normalizedToken;
    }

    const cachedFallback = this.getCachedValidToken();
    if (cachedFallback) {
      this.frontendLogger.warn(`[TokenService] getToken(): using cached token after ${reason}.`);
      return cachedFallback;
    }

    return '';
  }

  private cacheToken(token: string): void {
    this.cachedAccessToken = token;
    this.cachedAccessTokenExpMs = this.readTokenExpiry(token);
  }

  private getCachedValidToken(): string | null {
    if (!this.cachedAccessToken) {
      return null;
    }

    if (this.cachedAccessTokenExpMs !== null && Date.now() >= this.cachedAccessTokenExpMs - 60000) {
      this.cachedAccessToken = null;
      this.cachedAccessTokenExpMs = null;
      return null;
    }

    return this.cachedAccessToken;
  }

  private readTokenExpiry(token: string): number | null {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    try {
      const payload = JSON.parse(this.decodeBase64Url(parts[1])) as { exp?: unknown };
      if (typeof payload.exp === 'number' && Number.isFinite(payload.exp)) {
        return payload.exp * 1000;
      }
    } catch {
      this.frontendLogger.debug('[TokenService] getToken(): could not parse token expiry; keeping non-expiring cache fallback.');
    }

    return null;
  }

  private decodeBase64Url(value: string): string {
    const normalized = value.replaceAll('-', '+').replaceAll('_', '/');
    const padding = normalized.length % 4;
    const padded = padding === 0 ? normalized : normalized.padEnd(normalized.length + (4 - padding), '=');
    return globalThis.atob(padded);
  }
}
